import { useCallback, useEffect, useState } from "react";
import type { Qubic } from "api";
import { qubicsApi } from "api";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

type UseQubics = {
  qubics: Qubic[];
  status: LoadStatus;
  /**
   * Fetch again in the background. Ownership follows the company's limits and
   * keys, so a pane that shows it asks again when it is opened.
   */
  reload: () => void;
};

/**
 * The Qubics Store catalogue in display order, marked with what the company
 * owns. A user without a company still gets the whole catalogue, owning
 * nothing — that is the store's empty state, not an error.
 */
export const useQubics = (onSessionLost: () => void): UseQubics => {
  const [qubics, setQubics] = useState<Qubic[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");

  const load = useCallback(
    (background: boolean) => {
      let cancelled = false;

      qubicsApi
        .list()
        .then(({ data }) => {
          if (cancelled) return;
          setQubics(data.data);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            setStatus("unauthorized");
            return;
          }
          // A failed refresh keeps the catalogue already on screen.
          if (background) return;

          setStatus(isForbidden(error) ? "unverified" : "failed");
        });

      return () => {
        cancelled = true;
      };
    },
    [onSessionLost]
  );

  useEffect(() => load(false), [load]);

  const reload = useCallback(() => {
    load(true);
  }, [load]);

  return { qubics, status, reload };
};
